import { Injectable } from '@angular/core';
import { State, Store, Action, StateContext } from '@ngxs/store';
import { LocalizationService } from 'src/app/services/localization/localization.service';
import { AlertService } from 'src/app/shared/store/alert/alert.service';
import { FamilyActions } from '../../family/store/family/family.actions';
import { StudentEngagementData } from '../models/engagement-student.model';
import { EngagementActions } from './engagement.action';
import { EngagementService } from '../services/engagement.service';
import { patch } from '@ngxs/store/operators';
import { CopyEngagement } from '../models/copy-engagement.model';
import { MappingUtilsService } from 'src/app/services/utils/mapping-utils.service';
import { ZoneSelectors } from '../../zone/store/zone.selector';
import { ExerciseSelectors } from '../../exercise/store/exercise.selector';
import { CourseSelectors } from '../../course/store/course.selector';
import { SchoolEngagementData } from '../models/engagement-school.model';

export class EngagementStateModel {
  studentEngagementForm: {
    model: StudentEngagementData;
  };
  copyEngagementsForm: {
    model: CopyEngagement;
  };
  public schoolEngagement: SchoolEngagementData;
  public isLoadingEngagement: boolean;
  public error: string;
}

const defaults = {
  studentEngagementForm: {
    model: null,
  },
  copyEngagementsForm: {
    model: null,
  },
  schoolEngagement: null,
  isLoadingEngagement: false,
  error: null,
};

@State<EngagementStateModel>({
  name: 'engagements',
  defaults,
})
@Injectable()
export class EngagementState {
  constructor(
    private engagementService: EngagementService,
    private alertService: AlertService,
    private localizationService: LocalizationService,
    public store: Store
  ) {}

  @Action(EngagementActions.AddStudentEngagement)
  addStudentEngagement(ctx: StateContext<EngagementStateModel>, { user }: EngagementActions.AddStudentEngagement
  ) {
    const state = ctx.getState();
    let studentEngagementData = state.studentEngagementForm.model;
    this.alertService
      .confirm(
        this.getTitle(),
        this.localizationService.translate('engagements.studentEngagementAddConfirm'),
        this.localizationService.translate('buttons.accept'),
        this.localizationService.translate('buttons.cancel'),
        this.localizationService.translate('engagements.studentEngagementAddConfirm')
      )
      .then((result) => {
        if (result.value) {
          ctx.patchState({ isLoadingEngagement: true });
          return this.engagementService
            .addStudentEngagement(studentEngagementData, user)
            .subscribe({
              next: (engagementId) =>
                ctx.dispatch(
                  new EngagementActions.AddStudentEngagementSuccess(
                    engagementId
                  )
                ),
              error: (error) => {
                ctx.dispatch(new EngagementActions.Error(error));
              },
            });
        } else {
          return null;
        }
      });
  }

  @Action(EngagementActions.AddStudentEngagementSuccess)
  addStudentEngagementSuccess(ctx: StateContext<EngagementStateModel>, { engagementId }: EngagementActions.AddStudentEngagementSuccess) {
    ctx.setState(
      patch({
        isLoadingEngagement: false,
        studentEngagementForm: patch({
          model: patch({
            engagementId: engagementId,
          }),
        }),
      })
    );
  }

  @Action(EngagementActions.DeleteStudentEngagement)
  deleteStudentEngagement(ctx: StateContext<EngagementStateModel>, { engagementId, studentId }: EngagementActions.DeleteStudentEngagement) {
    this.alertService
      .confirm(
        this.getTitle(),
        this.localizationService.translate('engagements.studentEngagementDeleteConfirm'),
        this.localizationService.translate('buttons.accept'),
        this.localizationService.translate('buttons.cancel'),
        this.localizationService.translate('engagements.studentEngagementDeleteConfirm')
      )
      .then((result) => {
        if (result.value) {
          ctx.patchState({ isLoadingEngagement: true });
          return this.engagementService
            .deleteStudentEngagement(engagementId, studentId)
            .subscribe({
              next: () =>
                ctx.dispatch(
                  new EngagementActions.DeleteStudentEngagementSuccess(
                    studentId,
                    engagementId
                  )
                ),
              error: (error) => {
                ctx.dispatch(new EngagementActions.Error(error));
              },
            });
        } else {
          return null;
        }
      });
  }

  @Action(EngagementActions.DeleteStudentEngagementSuccess)
  deleteStudentEngagementSuccess(ctx: StateContext<EngagementStateModel>, { studentId, studentEngagementId}: EngagementActions.DeleteStudentEngagementSuccess
  ) {
    const state = ctx.getState();
    ctx.patchState({
      ...state,
      isLoadingEngagement: false,
    });
  }

  @Action(EngagementActions.GetSchoolEngagement)
  getSchoolEngagement(
    ctx: StateContext<EngagementStateModel>,
    { schoolId, serviceId, year, month, day }: EngagementActions.GetSchoolEngagement
  ) {
    ctx.patchState({ isLoadingEngagement: true, schoolEngagement: null });

    return this.engagementService
      .getSchoolEngagement(schoolId, serviceId, year, month, day)
      .subscribe({
        next: (response) => {
          ctx.dispatch(new EngagementActions.GetSchoolEngagementSuccess(response));
        },
        error: (error) => ctx.dispatch(new EngagementActions.Error(error)),
      });
  }

  @Action(EngagementActions.GetSchoolEngagementSuccess)
  getSchoolEngagementSuccess(
    ctx: StateContext<EngagementStateModel>,
    { schoolEngagement }: EngagementActions.GetSchoolEngagementSuccess
  ) {
    ctx.patchState({
      schoolEngagement: schoolEngagement,
      isLoadingEngagement: false,
    });
  }

  @Action(EngagementActions.CopyEngagements)
  copyEngagements(ctx: StateContext<EngagementStateModel>, { }: EngagementActions.CopyEngagements) {
    let state = ctx.getState();
    let copyEngagementsForm = state.copyEngagementsForm.model;

    let zones = this.store.selectSnapshot(ZoneSelectors.getZones);
    let exercises = this.store.selectSnapshot(ExerciseSelectors.getExercises);
    let courses = this.store.selectSnapshot(CourseSelectors.getCourses);

    let oldExcerciseDescription = exercises.find(exercise => exercise.exerciseId == copyEngagementsForm.oldExerciseId)?.description;
    let newExcerciseDescription = exercises.find(exercise => exercise.exerciseId == copyEngagementsForm.newExerciseId)?.description;
    state = ctx.getState();
    this.alertService.confirm(
      this.localizationService.translate("engagements.copyEngagements"),
      this.localizationService.translate('engagements.copyEngagementsConfirm'),
      this.localizationService.translate('buttons.accept'),
      this.localizationService.translate('buttons.cancel'),
      this.localizationService.translate('engagements.copyEngagementsConfirm')
      .replace('{0}', oldExcerciseDescription ).replace('{1}', newExcerciseDescription)
      .replace('{3}', this.getSelectedZoneNames(copyEngagementsForm.zones, zones)).replace('{4}', copyEngagementsForm.cutOffDate.toString())
      .replace('{5}', courses.find( x => x.courseId == copyEngagementsForm.lastCourse).courseDescription)
    ).then((result) => {
        if (result.value) {
          ctx.patchState({ isLoadingEngagement: true });
          const copyEngagementsFormRequest = this.getCopyEngagementsRequest(state.copyEngagementsForm.model);
          return this.engagementService.copyEngagements(copyEngagementsFormRequest).subscribe({
            next: () =>
              ctx.dispatch(new EngagementActions.CopyEngagementsSuccess()),
            error: (error) => {
              ctx.dispatch(new EngagementActions.Error(error));
            }
          });
        } else {
          return null;
        }
      });
  }

  private getCopyEngagementsRequest(model: CopyEngagement) {
    let copyEngagementsRequest = new CopyEngagement();
    copyEngagementsRequest.zones = model.zones;
    copyEngagementsRequest.newExerciseId = Number(model.newExerciseId);
    copyEngagementsRequest.oldExerciseId = Number(model.oldExerciseId);
    copyEngagementsRequest.cutOffDate = model.cutOffDate;
    copyEngagementsRequest.lastCourse = Number(model.lastCourse);

    return copyEngagementsRequest;
  }

  @Action(EngagementActions.CopyEngagementsSuccess)
  copyEngagementsSuccess(ctx: StateContext<EngagementStateModel>, { }: EngagementActions.CopyEngagementsSuccess) {
    ctx.patchState({
      isLoadingEngagement: false,
    });
  }

  @Action(EngagementActions.SetStudentEngagementDefaultState)
  setDefaultState(
    ctx: StateContext<EngagementStateModel>,
    {}: EngagementActions.SetStudentEngagementDefaultState
  ) {
    const state = ctx.getState();
    ctx.patchState({
      ...state, studentEngagementForm: defaults.studentEngagementForm
    });
  }

  @Action(EngagementActions.SetCopyEngagementsFormExercises)
  setCopyEngagementsFormExercises(ctx: StateContext<EngagementStateModel>, {newExerciseId, oldExerciseId}: EngagementActions.SetCopyEngagementsFormExercises) {
    ctx.setState(
      patch({
        copyEngagementsForm: patch({
          model: patch({
            newExerciseId: newExerciseId,
            oldExerciseId: oldExerciseId
          })
        })
      })
    );
  }

  @Action(EngagementActions.SetCopyEngagementsFormDefaultState)
  setCopyEngagementsFormDefaultState(ctx: StateContext<EngagementStateModel>, {}: EngagementActions.SetCopyEngagementsFormDefaultState) {
    ctx.setState(
      patch({
        copyEngagementsForm: defaults.copyEngagementsForm
      })
    );
  }

  @Action(EngagementActions.Error)
  onErrorOcurred(
    ctx: StateContext<EngagementStateModel>,
    { error }: EngagementActions.Error
  ) {
    ctx.patchState({
      error: error,
      isLoadingEngagement: false,
    });
    MappingUtilsService.showError(this.getTitle(), error, this.alertService, this.localizationService);
  }

  private getSelectedZoneNames(zones: number[], zonesMap: import("../../zone/models/zone.model").Zone[]): string {
    return zones.map(zoneId => zonesMap.find(zone => zone.code === zoneId).description).join(', ');
  }

  private getTitle() {
    return this.localizationService.translate('labels.student-engagement');
  }
}
